import type { Address } from "viem"
import type { Config } from "wagmi"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import { EventEmitter, makeGroupId } from "@/domains/library"
import { LockDurationMultiExtendedEvent } from "@/domains/lock/events"
import { maxLockMultiple } from "@/domains/on-chain"

interface MaxLockInteractionParams {
  account: Address
  chainId: number
  config: Config
  superVoterAddress: Address
  nftIds: Array<number>
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
}

export const maxLockMultipleInteraction = async ({
  account,
  chainId,
  config,
  superVoterAddress,
  nftIds,
  emitter,
  eventBus,
}: MaxLockInteractionParams) => {
  const result = await maxLockMultiple({
    account,
    chainId,
    config,
    superVoterAddress,
    nftIds,
  })

  if (result.success) {
    // Generate a unique group ID for this interaction instance
    const groupId = makeGroupId("max-lock-multiple")
    
    // Max lock extends duration by 26 weeks (7 * 26 * 86400 seconds)
    const secondsExtended = 7 * 26 * 86400
    
    // Publish ONE multi event with all NFT IDs to trigger lock refresh
    // This ensures only one RPC call is made (loadUserNfts fetches all NFTs in one call)
    eventBus.publish(new LockDurationMultiExtendedEvent(groupId, nftIds, secondsExtended, result.tx))
    
    emitter.emit("max-lock", {
      title: "Max Locked",
      description: `NFTs #${nftIds.join(", ")} max locked`,
      variant: "success",
    })
  }

  if (result.success === false) {
    emitter.emit("error-max-locking", {
      title: "Max lock failed",
      description: `Unable to max lock NFT #${nftIds.join(", ")}, reason ${result.error}`,
      variant: "error",
    })
  }

  return result
}
